/** @format */
import { Injectable } from '@angular/core';
import { Module, Urn } from 'common-typescript/types';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { isDegreeProgramme } from 'sis-components';
import { SelectedValidatablePlanService } from '../service/selected-validatable-plan.service';

const DEGREE_PROGRAM_SORT_ORDER: readonly Urn[] = [
    'urn:code:degree-program-type:doctor',
    'urn:code:degree-program-type:lic',
    'urn:code:degree-program-type:masters-degree',
    'urn:code:degree-program-type:bachelors-degree',
];

@Injectable()
export class FirstLevelModuleService {
    /**
     * Emits an ordered list of first level modules under the root of {@link SelectedValidatablePlanService.selectedValidatablePlan$}.
     */
    readonly firstLevelModules$: Observable<readonly Module[]>;

    constructor(selectedValidatablePlanService: SelectedValidatablePlanService) {
        this.firstLevelModules$ = selectedValidatablePlanService.selectedValidatablePlan$.pipe(
            map(validatablePlan => {
                if (validatablePlan?.rootModule) {
                    const modules: Module[] = validatablePlan.getSelectedModulesUnderModule(validatablePlan.rootModule);
                    return modules.length > 0 ? sortModulesByDegreeProgrammeType(modules) : [];
                }

                return [];
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
    }
}

/**
 * Sort modules of type degreeProgramme according to DEGREE_PROGRAM_SORT_ORDER in descending order based on index.
 * Meaning that types same as last index from DEGREE_PROGRAM_SORT_ORDER will appear first in the module listing.
 * All other types of modules not found in DEGREE_PROGRAM_SORT_ORDER will be sorted last.
 */
function sortModulesByDegreeProgrammeType(moduleList: Module[]): Module[] {
    return moduleList.toSorted((m1, m2) => {
        const m1SortValue = isDegreeProgramme(m1) ? DEGREE_PROGRAM_SORT_ORDER.indexOf(m1.degreeProgramTypeUrn) : -1;
        const m2SortValue = isDegreeProgramme(m2) ? DEGREE_PROGRAM_SORT_ORDER.indexOf(m2.degreeProgramTypeUrn) : -1;
        return m2SortValue - m1SortValue;
    });
}
